/**
 * Live microphone input level for the Avatar Lab.
 *
 * Answers the one question a "my mic might be broken" sysadmin has: is my voice
 * actually reaching the room? The level comes from the PUBLISHED LiveKit mic
 * track (see useAvatarLabCall), so a moving bar proves capture end-to-end, and
 * the resolved device label says WHICH input won.
 *
 * Deliberately not the shared <Progress>: that's a progress bar (monotonic, with
 * a ~150ms transition) and its easing visibly lags a value updating every 50ms,
 * which reads as a mushy, delayed meter. This is a meter — instantaneous, with
 * smoothing already applied at the source — and carries the proper `meter` role.
 */
import { useTranslation } from 'react-i18next';
import { Mic, MicOff } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface MicLevelMeterProps {
  /** Smoothed input level, 0..1. */
  level: number;
  /** False before the mic track is published — the meter renders inert. */
  active: boolean;
  /** Resolved input-device label, when the browser exposes one. */
  deviceLabel?: string | null;
  /**
   * i18n key prefix under the `avatars` namespace for the `.mic.*` labels.
   * Defaults to the sysadmin Avatar Lab (`lab`); the tenant agent call passes
   * `call` for its user-facing copy.
   */
  keyPrefix?: string;
}

export function MicLevelMeter({ level, active, deviceLabel, keyPrefix = 'lab' }: MicLevelMeterProps) {
  const { t } = useTranslation('avatars');
  const percent = active ? Math.round(Math.min(1, Math.max(0, level)) * 100) : 0;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        {active ? (
          <Mic className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        ) : (
          <MicOff className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        )}
        <div
          role="meter"
          aria-label={t(`${keyPrefix}.mic.label`)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          className="h-2 w-full overflow-hidden rounded-full bg-muted"
        >
          <div
            className={cn('h-full rounded-full', active ? 'bg-success' : 'bg-transparent')}
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
      <p className="truncate text-xs text-muted-foreground">
        {active ? (deviceLabel ?? t(`${keyPrefix}.mic.unknownDevice`)) : t(`${keyPrefix}.mic.inactive`)}
      </p>
    </div>
  );
}
